import { useState } from 'react'
import { toCanvas } from 'html-to-image'
import { jsPDF } from 'jspdf'
import { X_AXIS_HEIGHT } from '../timeline-constants'
import logoSrc from '~/assets/appbar-logo.png'

const CANVAS_SCALE = 2
const PDF_MARGIN_MM = 5
const PDF_FOOTER_MM = 6
const PDF_TITLE_MM = 13

export const useExportPdf = ({ exportRef, farmName, farmLat, farmLng }) => {
  const [isExporting, setIsExporting] = useState(false)

  const exportPdf = async () => {
    if (!exportRef.current) return
    setIsExporting(true)

    try {
      const el = exportRef.current

      // html-to-image doesn't recurse into SVG foreignObject children when
      // inlining computed styles, so MUI Typography labels inside the timeline
      // SVG render without CSS. Pre-inline the critical styles before capture
      // and restore them afterwards so the live UI is unaffected.
      const restoreStyles = inlineForeignObjectStyles(el)
      let canvas
      try {
        canvas = await toCanvas(el, {
          pixelRatio: CANVAS_SCALE,
          backgroundColor: '#ffffff',
          skipFonts: true,
          style: { opacity: '1' },
        })
      } finally {
        restoreStyles()
      }

      const canvasW = canvas.width
      const canvasH = canvas.height

      // Header strip: the timeline X-axis (month/week labels)
      const headerCanvasPx = X_AXIS_HEIGHT * CANVAS_SCALE

      // jsPDF landscape letter
      const pdf = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'letter' })
      const pageW = pdf.internal.pageSize.getWidth()   // 279.4mm
      const pageH = pdf.internal.pageSize.getHeight()  // 215.9mm

      const m = PDF_MARGIN_MM
      const contentW = pageW - m * 2

      // Scale: canvas pixels → PDF mm
      const pxToMm = contentW / canvasW
      const headerMm = headerCanvasPx * pxToMm

      // Available body height per page
      const bodyPage1Mm = pageH - m * 2 - PDF_TITLE_MM - headerMm - PDF_FOOTER_MM
      const bodyPageNMm = pageH - m * 2 - headerMm - PDF_FOOTER_MM

      const bodyCanvasPx = canvasH - headerCanvasPx

      // Pre-calculate total pages
      const page1BodyPx = bodyPage1Mm / pxToMm
      const pageNBodyPx = bodyPageNMm / pxToMm
      let totalPages = 1
      let remaining = bodyCanvasPx - page1BodyPx
      while (remaining > 0) {
        totalPages++
        remaining -= pageNBodyPx
      }

      const timestamp = new Date().toLocaleString('en-US', {
        year: 'numeric', month: 'long', day: 'numeric',
        hour: '2-digit', minute: '2-digit',
      })
      const displayName = farmName || 'Farm'
      const safeFilename = displayName.replace(/[^a-zA-Z0-9_-]/g, '_')
      const coordStr = farmLat != null && farmLng != null
        ? `${Math.abs(farmLat).toFixed(4)}° ${farmLat >= 0 ? 'N' : 'S'}, ${Math.abs(farmLng).toFixed(4)}° ${farmLng >= 0 ? 'E' : 'W'}`
        : null
      const headerImgData = sliceCanvas(canvas, 0, headerCanvasPx)

      const logoImg = await loadImage(logoSrc).catch(() => null)

      let bodySourcePx = headerCanvasPx

      for (let page = 1; page <= totalPages; page++) {
        if (page > 1) pdf.addPage()

        let cursorY = m

        // Title — page 1 only
        if (page === 1) {
          pdf.setFontSize(13)
          pdf.setFont('helvetica', 'bold')
          pdf.setTextColor(30, 30, 30)
          pdf.text(`${displayName} Timeline`, m, cursorY + 5)
          if (coordStr) {
            pdf.setFontSize(7)
            pdf.setFont('helvetica', 'normal')
            pdf.setTextColor(120, 120, 120)
            pdf.text(coordStr, m, cursorY + 9)
          }
          cursorY += PDF_TITLE_MM
        }

        // Timeline header strip (repeated on every page)
        pdf.addImage(headerImgData, 'PNG', m, cursorY, contentW, headerMm)
        cursorY += headerMm

        // Body slice
        const availBodyMm = page === 1 ? bodyPage1Mm : bodyPageNMm
        const availBodyPx = availBodyMm / pxToMm
        const slicePx = Math.min(availBodyPx, canvasH - bodySourcePx)

        if (slicePx > 0) {
          const bodyImgData = sliceCanvas(canvas, bodySourcePx, Math.ceil(slicePx))
          pdf.addImage(bodyImgData, 'PNG', m, cursorY, contentW, slicePx * pxToMm)
          bodySourcePx += slicePx
        }

        // Footer
        const footerY = pageH - m + 1
        pdf.setFontSize(7)
        pdf.setFont('helvetica', 'normal')
        pdf.setTextColor(140, 140, 140)
        if (logoImg) {
          const logoH = 3.5
          const logoW = logoH * (logoImg.naturalWidth / logoImg.naturalHeight)
          pdf.addImage(logoImg, 'PNG', m, footerY - 3, logoW, logoH)
          pdf.text('Terra Planner', m + logoW + 1.5, footerY)
        } else {
          pdf.text('Terra Planner', m, footerY)
        }
        pdf.text(timestamp, pageW / 2, footerY, { align: 'center' })
        pdf.text(`Page ${page} of ${totalPages}`, pageW - m, footerY, { align: 'right' })
      }

      pdf.save(`${safeFilename}-Timeline-${Date.now()}.pdf`)
    } finally {
      setIsExporting(false)
    }
  }

  return { exportPdf, isExporting }
}

function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.onload = () => resolve(img)
    img.onerror = reject
    img.src = src
  })
}

function inlineForeignObjectStyles(root) {
  const restores = []
  root.querySelectorAll('foreignObject').forEach(fo => {
    fo.querySelectorAll('*').forEach(el => {
      if (!(el instanceof HTMLElement)) return
      const saved = el.getAttribute('style')
      const c = window.getComputedStyle(el)
      const props = [
        'color', 'font-size', 'font-family', 'font-weight', 'font-style',
        'line-height', 'text-align', 'white-space', 'overflow', 'text-overflow',
        'display', 'width', 'height', 'max-width', 'min-width',
        'padding-top', 'padding-right', 'padding-bottom', 'padding-left',
        'margin-top', 'margin-right', 'margin-bottom', 'margin-left',
        'background-color', 'opacity', 'visibility', 'box-sizing',
        'flex-direction', 'align-items', 'justify-content', 'border-radius',
      ]
      el.setAttribute('style', props.map(p => `${p}:${c.getPropertyValue(p)}`).join(';'))
      restores.push({ el, saved })
    })
  })
  return () => restores.forEach(({ el, saved }) => {
    if (saved === null) el.removeAttribute('style')
    else el.setAttribute('style', saved)
  })
}

function sliceCanvas(src, srcY, sliceH) {
  const out = document.createElement('canvas')
  out.width = src.width
  out.height = sliceH
  const ctx = out.getContext('2d')
  ctx.drawImage(src, 0, srcY, src.width, sliceH, 0, 0, src.width, sliceH)
  return out.toDataURL('image/png')
}
